import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { UserInfo } from "@/types/superAdmin";
import { useTranslation } from "react-i18next";

interface ForceSignOutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserInfo | null;
  onConfirm: (userId: string, scope: 'global' | 'local' | 'others') => void;
  isLoading: boolean;
}

export function ForceSignOutDialog({
  open,
  onOpenChange,
  user,
  onConfirm,
  isLoading,
}: ForceSignOutDialogProps) {
  const { t } = useTranslation(["superadmin", "common"]);
  const [scope, setScope] = useState<'global' | 'local' | 'others'>('global');

  if (!user) return null;

  const fullName = `${user.first_name} ${user.last_name}`.trim() || user.email;

  const scopeDescriptions = {
    global: t("forceSignOut.scopeGlobalDesc"),
    local: t("forceSignOut.scopeLocalDesc"),
    others: t("forceSignOut.scopeOthersDesc"),
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("forceSignOut.title")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("forceSignOut.confirm")} <strong>{fullName}</strong> ?
            {t("forceSignOut.confirmSuffix")}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="py-4">
          <Label className="text-sm font-medium mb-3 block">{t("forceSignOut.scope")}</Label>
          <RadioGroup value={scope} onValueChange={(value) => setScope(value as typeof scope)}>
            <div className="flex items-start space-x-2 mb-3">
              <RadioGroupItem value="global" id="global" />
              <div className="space-y-1">
                <Label htmlFor="global" className="font-medium cursor-pointer">
                  {t("forceSignOut.scopeGlobal")}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {scopeDescriptions.global}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-2 mb-3">
              <RadioGroupItem value="local" id="local" />
              <div className="space-y-1">
                <Label htmlFor="local" className="font-medium cursor-pointer">
                  {t("forceSignOut.scopeLocal")}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {scopeDescriptions.local}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <RadioGroupItem value="others" id="others" />
              <div className="space-y-1">
                <Label htmlFor="others" className="font-medium cursor-pointer">
                  {t("forceSignOut.scopeOthers")}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {scopeDescriptions.others}
                </p>
              </div>
            </div>
          </RadioGroup>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>{t("common:cancel")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => onConfirm(user.user_id, scope)}
            disabled={isLoading}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isLoading ? t("forceSignOut.disconnecting") : t("forceSignOut.disconnect")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}